import { createSlice, current, PayloadAction } from '@reduxjs/toolkit';
import type {
  HistoryStep,
  InterviewState,
  WorkItemType,
  IPEResponse,
  DeferredItems,
} from './types';
import { makeStepKey, makeGroupInstanceKey } from './stepKey';
import { computeVisibleQuestionQueue } from './questionQueue';

const _a = (
  arr: unknown[] | null | undefined,
  showValues?: boolean
): string => {
  if (arr == null) {
    return '-';
  }
  const len = arr.length;
  if (showValues && len <= 8) {
    return `[${len} ${arr.join(',')}]`;
  }
  return `[${len}]`;
};

const _ag = (ag: Record<number, number[]> | null | undefined): string => {
  if (ag == null) {
    return '-';
  }
  const keys = Object.keys(ag);
  if (keys.length === 0) {
    return '{}';
  }
  return '{' + keys.map((k) => `${k}:${_a(ag[Number(k)])}`).join(', ') + '}';
};

/**
 * Splice the given qitId from the active answer_group's QIT list in
 * answerGroupQits. If the QIT still exists in another answer_group, re-add
 * it to onlyQitIds so it gets re-presented for that instance.
 *
 * Used by both recordLocalAnswer and skipQuestion to handle the
 * same-QIT-across-instances case without duplicating the logic.
 */
function spliceQitFromActiveAnswerGroup(
  state: InterviewState,
  qitId: number
): void {
  if (!state.answerGroupQits || state.activeAgTarget == null) {
    return;
  }
  const agQitIds = state.answerGroupQits[state.activeAgTarget];
  if (!agQitIds) {
    return;
  }
  state.answerGroupQits[state.activeAgTarget] = agQitIds.filter(
    (id) => id !== qitId
  );
  if (state.answerGroupQits[state.activeAgTarget].length === 0) {
    delete state.answerGroupQits[state.activeAgTarget];
    state.activeAgTarget = null;
    if (Object.keys(state.answerGroupQits).length === 0) {
      state.answerGroupQits = null;
    }
  }
  if (state.onlyQitIds) {
    let stillNeeded = false;
    if (state.answerGroupQits) {
      for (const qitIds of Object.values(state.answerGroupQits)) {
        if (qitIds.includes(qitId)) {
          stillNeeded = true;
          break;
        }
      }
    }
    if (stillNeeded && !state.onlyQitIds.includes(qitId)) {
      state.onlyQitIds.push(qitId);
    }
  }
}

const initialState: InterviewState = {
  surveyId: null,
  onBehalfOf: null,
  onlyQitIds: null,
  answerGroupQits: null,
  activeAgTarget: null,
  certId: null,
  certName: null,
  propertyName: null,
  certCategory: null,
  effectiveDate: null,
  canAnswer: false,
  canSign: false,
  workItemType: 'loading',
  errorMessage: null,
  backPath: null,
  requestedWorkItem: null,
  hasStartedInterview: false,
  currentGroup: null,
  visibleQuestionQueue: [],
  currentQuestionIndex: 0,
  localAnswers: {},
  answersByKey: {},
  currentAnswerGroupId: null,
  deferred: {
    qitIds: {},
    signatureIds: [],
    staleDocIds: [],
    profileFieldNames: [],
    fileUploadDismissed: false,
  },
  pendingSignatures: [],
  staleDocuments: [],
  userProfileFields: [],
  pendingFileQits: [],
  interviewComplete: false,
  history: [],
  cursor: 0,
  verifiedIndex: 0,
  groupsById: {},
  instanceSeqByGroup: {},
  profileFieldsByName: {},
  frontierResyncNeeded: false,
  loading: false,
  loadingProgress: 0,
  answerInFlight: false,
  answerError: null,
};

/**
 * Determine which work item type should be shown, given the current IPE
 * response and the current deferred lists.
 *
 * Priority: signature > stale_document > file_upload > user_profile_field >
 *            question > skip_gate > complete/nothing_to_do
 */
function resolveWorkItemType(
  response: IPEResponse,
  deferred: DeferredItems,
  hasStartedInterview: boolean,
  requestedWorkItem: 'questions' | null
): WorkItemType {
  const pendingSignatures = response.pending_signatures ?? [];
  const staleDocuments = response.stale_documents ?? [];
  const userProfileFields = response.user_profile_fields ?? [];
  const pendingFileQits = response.pending_file_qits ?? [];
  const interviewComplete = response.interview_complete ?? false;
  const nextGroup = response.next_group ?? null;

  // ENG-4049: an entry that asked for questions (the cert-status "Complete
  // about N questions" row) opts out of the signature-first priority while the
  // survey still has a group to ask. Requiring next_group is what makes the
  // intent self-terminating: once the questions are exhausted the signature
  // screen comes back on its own, with no extra reset.
  const questionsRequested =
    requestedWorkItem === 'questions' && nextGroup != null;

  // Non-deferred signatures take priority — only when the current user can sign.
  // When can_sign=false (proxy/on-behalf-of mode) the actor cannot sign on the
  // subject's behalf, so the signature work item is bypassed entirely.
  // Skipping it here (rather than deferring) ensures nothing lands in
  // deferred.signatureIds, which would otherwise trigger the skip_gate.
  // Default to true when can_sign is absent (backward-compatible; all real
  // backend responses include this field).
  const canSign = response.can_sign ?? true;
  const nonDeferredSigs = canSign
    ? pendingSignatures.filter(
        (sig) => !deferred.signatureIds.includes(sig.signature_request_id)
      )
    : [];
  if (nonDeferredSigs.length > 0 && !questionsRequested) {
    return 'signature';
  }

  // Non-deferred stale documents
  const nonDeferredStaleDocs = staleDocuments.filter(
    (doc) => !deferred.staleDocIds.includes(doc.survey_file_id)
  );
  if (nonDeferredStaleDocs.length > 0) {
    return 'stale_document';
  }

  // Pending file QITs (session-deferrable by user)
  if (pendingFileQits.length > 0 && !deferred.fileUploadDismissed) {
    return 'file_upload';
  }

  // Non-deferred user profile fields
  const nonDeferredProfileFields = userProfileFields.filter(
    (field) => !deferred.profileFieldNames.includes(field.field_name)
  );
  if (nonDeferredProfileFields.length > 0) {
    return 'user_profile_field';
  }

  // Next group of questions
  if (nextGroup) {
    return 'question';
  }

  // fileUploadDismissed gates the file_upload screen at line 103 but
  // does NOT gate interview progress — the system auto-defers it on
  // cert-hub "Continue interview", not the user.
  const fileUploadStillPending =
    pendingFileQits.length > 0 && !interviewComplete;
  const hasDeferred =
    Object.values(deferred.qitIds).some((arr) => arr.length > 0) ||
    deferred.signatureIds.length > 0 ||
    deferred.staleDocIds.length > 0 ||
    deferred.profileFieldNames.length > 0 ||
    fileUploadStillPending;
  if (hasDeferred) {
    return 'skip_gate';
  }

  // Interview complete
  if (interviewComplete) {
    return hasStartedInterview ? 'complete' : 'nothing_to_do';
  }

  // Proxy mode: all answerable work is done but pending_signatures remain.
  // The backend will not set interview_complete=true while signatures are
  // outstanding, but the proxy cannot sign on the subject's behalf — they
  // have finished everything they can do. Show the signature screen one final
  // time so the user sees which documents still need signing, then the
  // "Continue" button in SignatureScreen dispatches setWorkItemType('complete')
  // directly (bypassing a redundant IPE call that would just loop here again).
  if (!canSign && pendingSignatures.length > 0) {
    return 'signature';
  }

  return 'loading';
}

const interviewSlice = createSlice({
  name: 'interview',
  initialState,
  reducers: {
    initInterview(
      state,
      action: PayloadAction<{
        surveyId: number;
        onBehalfOf?: number | null;
        onlyQitIds?: number[] | null;
        answerGroupQits?: Record<number, number[]> | null;
        backPath?: string | null;
        requestedWorkItem?: 'questions' | null;
      }>
    ) {
      state.surveyId = action.payload.surveyId;
      state.onBehalfOf = action.payload.onBehalfOf ?? null;
      state.onlyQitIds = action.payload.onlyQitIds ?? null;
      state.answerGroupQits = action.payload.answerGroupQits ?? null;
      state.activeAgTarget = null;
      state.backPath = action.payload.backPath ?? null;
      state.requestedWorkItem = action.payload.requestedWorkItem ?? null;
      state.loading = true;
      state.workItemType = 'loading';
    },

    // ENG-2968: applied after initInterview once Redux certs resolve — sends
    // the header back button to the certification hub for progressed certs.
    setBackPath(state, action: PayloadAction<string | null>) {
      state.backPath = action.payload;
    },

    advanceToNextAnswerGroup(
      state,
      action: PayloadAction<{ nextAgId: number; nextQitIds: number[] }>
    ) {
      state.activeAgTarget = action.payload.nextAgId;
      state.onlyQitIds = action.payload.nextQitIds;
    },

    /**
     * Like advanceToNextAnswerGroup but does NOT set onlyQitIds — used for
     * auto-advancing through answer groups in the normal interview flow,
     * where the backend already receives enough QIT context via
     * answer_group_qits and the frontend should not force all QITs visible.
     */
    activateAnswerGroup(
      state,
      action: PayloadAction<{ nextAgId: number; nextQitIds: number[] }>
    ) {
      state.activeAgTarget = action.payload.nextAgId;
    },

    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },

    setLoadingProgress(state, action: PayloadAction<number>) {
      state.loadingProgress = action.payload;
    },

    handleIPEResponse(state, action: PayloadAction<IPEResponse>) {
      const response = action.payload;
      if (process.env.NODE_ENV !== 'production') {
        const s = current(state);
        const ng = response.next_group;
        const wit = resolveWorkItemType(
          response,
          s.deferred,
          s.hasStartedInterview,
          s.requestedWorkItem
        );
        console.log(
          `[IPE<<] group=${ng?.group_id ?? '-'}:${ng?.group_type ?? '-'}  ` +
            `work=${wit}  ` +
            `ag_ids=${_a(ng?.answer_group_ids, true)}  ` +
            `agQits=${_ag(s.answerGroupQits)}  ` +
            `only=${_a(s.onlyQitIds, true)}  ` +
            `complete=${response.interview_complete ?? false}`
        );
      }
      // Update identity / context fields when present
      if (response.cert_id !== undefined) {
        state.certId = response.cert_id;
      }
      if (response.cert_name !== undefined) {
        state.certName = response.cert_name;
      }
      if (response.property_name !== undefined) {
        state.propertyName = response.property_name;
      }
      if (response.cert_category !== undefined) {
        state.certCategory = response.cert_category;
      }
      if (response.effective_date !== undefined) {
        state.effectiveDate = response.effective_date;
      }

      // Permissions
      if (response.can_answer !== undefined) {
        state.canAnswer = response.can_answer;
      }
      if (response.can_sign !== undefined) {
        state.canSign = response.can_sign;
      }

      // IPE data
      state.pendingSignatures =
        response.pending_signatures ?? state.pendingSignatures;
      state.staleDocuments = response.stale_documents ?? state.staleDocuments;
      state.userProfileFields =
        response.user_profile_fields ?? state.userProfileFields;
      if (response.user_profile_fields) {
        for (const f of response.user_profile_fields) {
          state.profileFieldsByName[f.field_name] = f;
        }
      }
      state.pendingFileQits =
        response.pending_file_qits ?? state.pendingFileQits;
      state.interviewComplete = response.interview_complete ?? false;

      // Determine work item type using current deferred lists
      const workItemType = resolveWorkItemType(
        response,
        state.deferred,
        state.hasStartedInterview,
        state.requestedWorkItem
      );
      state.workItemType = workItemType;

      // If showing questions, load the group
      if (workItemType === 'question' && response.next_group) {
        state.currentGroup = response.next_group;

        // Populate answerGroupQits for multi-groups so the interview can
        // auto-advance through all remaining answer groups without the
        // user needing to click "Yes, add another".
        const agIds = response.next_group.answer_group_ids;
        if (
          agIds &&
          agIds.length > 0 &&
          response.next_group.group_type === 'multi' &&
          !state.answerGroupQits
        ) {
          const allQitIds =
            response.next_group.questions?.map((q: any) => q.qit_id) ?? [];
          const agQits: Record<number, number[]> = {};
          for (const agId of agIds) {
            agQits[agId] = [...allQitIds];
          }
          state.answerGroupQits = agQits;
        }

        // Capture answer_group_id from response (for resuming multi-group instances)
        const groupAnswerGroupId =
          response.next_group.questions[0]?.answer_group_id ?? null;

        // Track which answer_group we're currently targeting so that
        // recordLocalAnswer / skipQuestion only splice from this instance's
        // QIT list, not from all instances (handles same-QIT-across-instances).
        if (state.answerGroupQits && groupAnswerGroupId != null) {
          state.activeAgTarget = groupAnswerGroupId;
        }

        // Retain a PER-INSTANCE group snapshot for back-navigation re-render.
        // Keyed by (groupId, instance identity) so reviewing an older instance
        // of a multi group renders its own instance_label / existing_answer —
        // see makeGroupInstanceKey.
        const gid = response.next_group.group_id;
        if (state.instanceSeqByGroup[gid] === undefined) {
          state.instanceSeqByGroup[gid] = 0;
        }
        state.groupsById[
          makeGroupInstanceKey(
            gid,
            groupAnswerGroupId,
            state.instanceSeqByGroup[gid]
          )
        ] = response.next_group;

        state.localAnswers = {};
        const instanceDeferredQitIds =
          state.deferred.qitIds[
            makeGroupInstanceKey(
              gid,
              groupAnswerGroupId,
              state.instanceSeqByGroup[gid] ?? 0
            )
          ] || [];
        const visibleQueue = computeVisibleQuestionQueue(
          response.next_group.questions,
          {}, // no local answers on initial load
          instanceDeferredQitIds,
          (response.pending_file_qits ?? []).map((p) => p.qit_id),
          state.onlyQitIds
        );
        if (process.env.NODE_ENV !== 'production') {
          console.log(
            `[VIS] visible=${_a(visibleQueue, true)}  ` +
              `only=${_a(current(state).onlyQitIds, true)}  ` +
              `group=${_a(
                response.next_group.questions.map((q: any) => q.qit_id)
              )}`
          );
        }
        state.visibleQuestionQueue = visibleQueue;
        state.currentQuestionIndex = 0;
        state.currentAnswerGroupId = groupAnswerGroupId;

        // If the backend returned a group but every question is already answered
        // (or conditionally hidden), the visible queue will be empty. For multi-
        // instance groups this means the current instance is complete — pivot to
        // the "add another?" screen rather than rendering a blank question screen.
        // Skip this when answerGroupQits is active — the advance logic in
        // QuestionScreen will move to the next instance instead.
        if (
          visibleQueue.length === 0 &&
          response.next_group.group_type === 'multi'
        ) {
          if (process.env.NODE_ENV !== 'production') {
            console.log(
              `[VIS] empty-multi → ADD_ANOTHER  group=${response.next_group.group_id}:${response.next_group.group_type}`
            );
          }
          state.answerGroupQits = null;
          state.activeAgTarget = null;
          state.onlyQitIds = null;
          state.workItemType = 'add_another';
        }
      }

      // Mark interview as started on first successful response
      state.hasStartedInterview = true;
      state.loading = false;
      state.frontierResyncNeeded = false;
    },

    setWorkItemType(state, action: PayloadAction<WorkItemType>) {
      state.workItemType = action.payload;
    },

    setError(state, action: PayloadAction<string>) {
      state.workItemType = 'error';
      state.errorMessage = action.payload;
      state.loading = false;
    },

    advanceQuestion(state) {
      const nextIndex = state.currentQuestionIndex + 1;
      if (nextIndex < state.visibleQuestionQueue.length) {
        state.currentQuestionIndex = nextIndex;
      }
      // If past end, caller is responsible for handling group exhaustion
    },

    updateVisibleQueue(state, action: PayloadAction<number[]>) {
      state.visibleQuestionQueue = action.payload;
      state.currentQuestionIndex = 0;
    },

    recordLocalAnswer(
      state,
      action: PayloadAction<{ qitId: number; value: any }>
    ) {
      state.localAnswers[action.payload.qitId] = action.payload.value;

      const groupId = state.currentGroup?.group_id ?? null;
      const instanceSeq =
        groupId != null ? state.instanceSeqByGroup[groupId] ?? 0 : 0;

      const step: HistoryStep = {
        kind: 'question',
        groupId,
        answerGroupId: state.currentAnswerGroupId ?? null,
        instanceSeq,
        qitId: action.payload.qitId,
        fieldName: null,
        status: 'answered',
      };
      const key = makeStepKey(step);
      state.answersByKey[key] = action.payload.value;

      // Flip skipped → answered if this QIT was previously skipped,
      // and remove it from deferred.qitIds so the skip-gate won't re-block it.
      const skippedIndex = state.history.findIndex(
        (h) => makeStepKey(h) === key && h.status === 'skipped'
      );
      if (skippedIndex !== -1) {
        state.history[skippedIndex].status = 'answered';
      }
      // Always clear from deferred regardless of history match —
      // resetInterview preserves deferred.qitIds but wipes history,
      // so an answer recorded after a remount would never clear the
      // phantom skipped qitIds that were answered in a prior session.
      for (const key of Object.keys(state.deferred.qitIds)) {
        state.deferred.qitIds[key] = state.deferred.qitIds[key].filter(
          (id) => id !== action.payload.qitId
        );
        if (state.deferred.qitIds[key].length === 0) {
          delete state.deferred.qitIds[key];
        }
      }

      // Upsert guard: only append if no history step already has this key
      const exists = state.history.some((h) => makeStepKey(h) === key);
      if (!exists) {
        state.history.push(step);
        state.cursor = state.history.length;
        state.verifiedIndex = Math.max(
          state.verifiedIndex,
          state.history.length - 1
        );
      }

      // In re-answer mode, remove the answered QIT from onlyQitIds so subsequent
      // IPE calls don't re-present it (the backend treats onlyQitIds as always-unanswered).
      if (state.onlyQitIds) {
        state.onlyQitIds = state.onlyQitIds.filter(
          (id) => id !== action.payload.qitId
        );
      }
      // Splice from the active answer_group's QIT list only (not from all
      // instances — handles the same-QIT-across-instances case). If the QIT
      // still exists in another answer_group, re-add it to onlyQitIds so it
      // gets re-presented for that instance.
      spliceQitFromActiveAnswerGroup(state, action.payload.qitId);

      if (process.env.NODE_ENV !== 'production') {
        console.log(
          `[ANS] qit=${action.payload.qitId}  ` +
            `only=${_a(state.onlyQitIds, true)}  ` +
            `agQits=${_ag(state.answerGroupQits)}  ` +
            `activeAg=${state.activeAgTarget ?? '-'}`
        );
      }
    },

    skipQuestion(state, action: PayloadAction<number>) {
      const qitId = action.payload;
      const groupId = state.currentGroup?.group_id ?? null;
      const instanceSeq =
        groupId != null ? state.instanceSeqByGroup[groupId] ?? 0 : 0;
      const instanceKey = makeGroupInstanceKey(
        groupId,
        state.currentAnswerGroupId ?? null,
        instanceSeq
      );
      if (!state.deferred.qitIds[instanceKey]) {
        state.deferred.qitIds[instanceKey] = [];
      }
      if (!state.deferred.qitIds[instanceKey].includes(qitId)) {
        state.deferred.qitIds[instanceKey].push(qitId);
      }
      if (state.onlyQitIds) {
        state.onlyQitIds = state.onlyQitIds.filter((id) => id !== qitId);
      }
      spliceQitFromActiveAnswerGroup(state, qitId);

      // Append a history step so skipped questions are navigable
      const step: HistoryStep = {
        kind: 'question',
        groupId,
        answerGroupId: state.currentAnswerGroupId ?? null,
        instanceSeq,
        qitId,
        fieldName: null,
        status: 'skipped',
      };
      const key = makeStepKey(step);

      const existingIndex = state.history.findIndex(
        (h) => makeStepKey(h) === key
      );
      if (existingIndex === -1) {
        state.history.push(step);
        state.cursor = state.history.length;
        state.verifiedIndex = Math.max(
          state.verifiedIndex,
          state.history.length - 1
        );
      } else {
        state.history[existingIndex].status = 'skipped';
      }

      // Also advance past the skipped question
      const nextIndex = state.currentQuestionIndex + 1;
      if (nextIndex < state.visibleQuestionQueue.length) {
        state.currentQuestionIndex = nextIndex;
      }
    },

    deferSignatures(state, action: PayloadAction<number[]>) {
      for (const sigId of action.payload) {
        if (!state.deferred.signatureIds.includes(sigId)) {
          state.deferred.signatureIds.push(sigId);
        }
      }
    },

    deferStaleDoc(state, action: PayloadAction<number>) {
      if (!state.deferred.staleDocIds.includes(action.payload)) {
        state.deferred.staleDocIds.push(action.payload);
      }
    },

    deferProfileField(state, action: PayloadAction<string>) {
      if (!state.deferred.profileFieldNames.includes(action.payload)) {
        state.deferred.profileFieldNames.push(action.payload);
      }
    },

    recordProfileStep(
      state,
      action: PayloadAction<{ fieldName: string; value: unknown }>
    ) {
      const step: HistoryStep = {
        kind: 'user_profile_field',
        groupId: null,
        answerGroupId: null,
        instanceSeq: 0,
        qitId: null,
        fieldName: action.payload.fieldName,
        status: 'answered',
      };
      const key = makeStepKey(step);
      state.answersByKey[key] = action.payload.value;

      state.deferred.profileFieldNames =
        state.deferred.profileFieldNames.filter(
          (n) => n !== action.payload.fieldName
        );

      const idx = state.history.findIndex(
        (h) =>
          h.kind === 'user_profile_field' &&
          h.fieldName === action.payload.fieldName
      );
      if (idx === -1) {
        state.history.push(step);
        state.cursor = state.history.length;
        state.verifiedIndex = Math.max(
          state.verifiedIndex,
          state.history.length - 1
        );
      } else {
        state.history[idx].status = 'answered';
      }
    },

    skipProfileStep(state, action: PayloadAction<{ fieldName: string }>) {
      if (
        !state.deferred.profileFieldNames.includes(action.payload.fieldName)
      ) {
        state.deferred.profileFieldNames.push(action.payload.fieldName);
      }
      const step: HistoryStep = {
        kind: 'user_profile_field',
        groupId: null,
        answerGroupId: null,
        instanceSeq: 0,
        qitId: null,
        fieldName: action.payload.fieldName,
        status: 'skipped',
      };
      const existingIndex = state.history.findIndex(
        (h) =>
          h.kind === 'user_profile_field' &&
          h.fieldName === action.payload.fieldName
      );
      if (existingIndex === -1) {
        state.history.push(step);
        state.cursor = state.history.length;
        state.verifiedIndex = Math.max(
          state.verifiedIndex,
          state.history.length - 1
        );
      } else {
        state.history[existingIndex].status = 'skipped';
      }
    },

    deferFileUpload(state) {
      state.deferred.fileUploadDismissed = true;
    },

    clearAllDeferred(state) {
      state.deferred = {
        qitIds: {},
        signatureIds: [],
        staleDocIds: [],
        profileFieldNames: [],
        fileUploadDismissed: false,
      };
    },

    clearAnswerGroupState(state) {
      state.answerGroupQits = null;
      state.activeAgTarget = null;
      state.onlyQitIds = null;
    },

    resetGroupState(state) {
      state.currentGroup = null;
      state.visibleQuestionQueue = [];
      state.currentQuestionIndex = 0;
      state.localAnswers = {};
      state.currentAnswerGroupId = null;
    },

    startNewGroupInstance(state) {
      // Keep currentGroup intact — same group, fresh instance
      const groupId = state.currentGroup?.group_id ?? null;

      // Increment instance counter so recordLocalAnswer produces distinct keys
      if (groupId != null) {
        state.instanceSeqByGroup[groupId] =
          (state.instanceSeqByGroup[groupId] ?? 0) + 1;
      }

      state.localAnswers = {};
      state.currentAnswerGroupId = null;
      state.currentQuestionIndex = 0;
      state.workItemType = 'question';
      // Build the fresh instance as a CLEARED COPY — never mutate the previous
      // instance's snapshot in place. currentGroup and its groupsById snapshot
      // share the same object reference (handleIPEResponse assigns both), so
      // in-place clearing would wipe instance_label / existing_answer for the
      // instance the user may navigate back to.
      if (state.currentGroup) {
        const cleared = {
          ...state.currentGroup,
          instance_label: null,
          questions: state.currentGroup.questions.map((q) => ({
            ...q,
            existing_answer: null,
          })),
        };
        state.currentGroup = cleared;
        if (groupId != null) {
          state.groupsById[
            makeGroupInstanceKey(
              groupId,
              null,
              state.instanceSeqByGroup[groupId]
            )
          ] = cleared;
        }
        state.visibleQuestionQueue = computeVisibleQuestionQueue(
          cleared.questions,
          {},
          state.deferred.qitIds[
            makeGroupInstanceKey(
              groupId,
              null,
              state.instanceSeqByGroup[groupId] ?? 0
            )
          ] || [],
          state.pendingFileQits.map((p) => p.qit_id),
          state.onlyQitIds
        );
      }
    },

    setAnswerInFlight(state, action: PayloadAction<boolean>) {
      state.answerInFlight = action.payload;
    },

    setAnswerError(state, action: PayloadAction<string | null>) {
      state.answerError = action.payload;
    },

    setAnswerGroupId(state, action: PayloadAction<number | null>) {
      const id = action.payload;
      const prev = state.currentAnswerGroupId;
      state.currentAnswerGroupId = id;

      // Backfill on lazy creation (null → real id): earlier steps of this
      // instance were recorded with answerGroupId=null and keyed by
      // instanceSeq (e.g. a skipped first question, or steps recorded before
      // the first POST resolved). Unify the instance identity now — update
      // the steps, re-key their answersByKey entries, and migrate the group
      // snapshot key — so isSameInstance, the skipped→answered flip in
      // recordLocalAnswer, and reviewing-edit POSTs (step.answerGroupId) all
      // see one consistent instance.
      if (id == null || prev != null) {
        return;
      }
      const gid = state.currentGroup?.group_id ?? null;
      if (gid == null) {
        return;
      }
      const seq = state.instanceSeqByGroup[gid] ?? 0;

      for (const step of state.history) {
        if (
          step.kind === 'question' &&
          step.groupId === gid &&
          step.instanceSeq === seq &&
          step.answerGroupId == null
        ) {
          const oldKey = makeStepKey(step);
          step.answerGroupId = id;
          const newKey = makeStepKey(step);
          if (oldKey in state.answersByKey) {
            state.answersByKey[newKey] = state.answersByKey[oldKey];
            delete state.answersByKey[oldKey];
          }
        }
      }

      const oldSnapKey = makeGroupInstanceKey(gid, null, seq);
      if (state.groupsById[oldSnapKey]) {
        state.groupsById[makeGroupInstanceKey(gid, id, seq)] =
          state.groupsById[oldSnapKey];
        delete state.groupsById[oldSnapKey];
      }
    },

    setFrontierResyncNeeded(state, action: PayloadAction<boolean>) {
      state.frontierResyncNeeded = action.payload;
    },

    resetInterview(state) {
      // Preserve `deferred` (session-level user intent — "I'll skip this
      // for now") across interview-route remounts. Otherwise React's
      // deferred passive cleanup of an outgoing route can wipe the flag a
      // user just set in handleSkip on the file-upload screen, causing the
      // IPE response to re-surface the file_upload work item and bounce
      // them right back to /renter/files. The SkipGateScreen ("Answer
      // Now") still provides a clean way to un-defer when the user is
      // ready to come back to the skipped items.
      return { ...initialState, deferred: state.deferred };
    },

    // ── Task 4: History / cursor / verified navigation reducers ──────────

    appendHistoryStep(state, action: PayloadAction<HistoryStep>) {
      state.history.push(action.payload);
      state.cursor = state.history.length;
      state.verifiedIndex = Math.max(
        state.verifiedIndex,
        state.history.length - 1
      );
    },

    moveCursorBack(state) {
      state.cursor = Math.max(0, state.cursor - 1);
    },

    moveCursorForward(state) {
      state.cursor = Math.min(state.history.length, state.cursor + 1);
    },

    invalidateFrom(state, action: PayloadAction<number>) {
      state.verifiedIndex = action.payload;
    },

    confirmStep(state, action: PayloadAction<number>) {
      if (action.payload > state.verifiedIndex) {
        state.verifiedIndex = action.payload;
      }
    },

    dropStep(state, action: PayloadAction<number>) {
      const idx = action.payload;
      if (idx < 0 || idx >= state.history.length) {
        return;
      }
      state.history.splice(idx, 1);
      if (state.cursor >= idx && state.cursor > 0) {
        state.cursor -= 1;
      }
      state.verifiedIndex = Math.min(
        state.verifiedIndex,
        state.history.length - 1
      );
    },

    setDurableAnswer(
      state,
      action: PayloadAction<{ key: string; value: any }>
    ) {
      state.answersByKey[action.payload.key] = action.payload.value;
    },

    markStepAnswered(state, action: PayloadAction<string>) {
      const key = action.payload;
      const idx = state.history.findIndex((h) => makeStepKey(h) === key);
      if (idx === -1) {
        return;
      }

      const step = state.history[idx];
      if (step.status === 'skipped') {
        state.history[idx].status = 'answered';
        if (step.qitId != null) {
          for (const key of Object.keys(state.deferred.qitIds)) {
            state.deferred.qitIds[key] = state.deferred.qitIds[key].filter(
              (id) => id !== step.qitId
            );
            if (state.deferred.qitIds[key].length === 0) {
              delete state.deferred.qitIds[key];
            }
          }
        }
      }
    },

    /**
     * Inverse of markStepAnswered: flip an answered step to skipped while
     * reviewing, so the reviewing-mode "Skip for now" button can re-defer a
     * question the renter navigated back to. The question is tracked as
     * skipped (deferred), but its durable answer is intentionally NOT cleared
     * — the backend keeps a persisted SurveyAnswer when a QIT is skipped, so
     * the FE must keep its local copy in answersByKey too.
     */
    markStepSkipped(state, action: PayloadAction<string>) {
      const key = action.payload;
      const idx = state.history.findIndex((h) => makeStepKey(h) === key);
      if (idx === -1) {
        return;
      }

      const step = state.history[idx];
      if (step.kind === 'question' && step.status === 'answered') {
        state.history[idx].status = 'skipped';
        if (step.qitId != null) {
          const instanceKey = makeGroupInstanceKey(
            step.groupId,
            step.answerGroupId,
            step.instanceSeq
          );
          if (!state.deferred.qitIds[instanceKey]) {
            state.deferred.qitIds[instanceKey] = [];
          }
          if (!state.deferred.qitIds[instanceKey].includes(step.qitId)) {
            state.deferred.qitIds[instanceKey].push(step.qitId);
          }
        }
      }
    },
  },
});

export const {
  initInterview,
  setLoading,
  setLoadingProgress,
  handleIPEResponse,
  setWorkItemType,
  setError,
  advanceQuestion,
  updateVisibleQueue,
  recordLocalAnswer,
  skipQuestion,
  deferSignatures,
  deferStaleDoc,
  deferProfileField,
  recordProfileStep,
  skipProfileStep,
  deferFileUpload,
  clearAllDeferred,
  resetGroupState,
  startNewGroupInstance,
  setAnswerInFlight,
  setAnswerError,
  setAnswerGroupId,
  resetInterview,
  appendHistoryStep,
  moveCursorBack,
  moveCursorForward,
  invalidateFrom,
  confirmStep,
  dropStep,
  setDurableAnswer,
  markStepAnswered,
  markStepSkipped,
  setFrontierResyncNeeded,
  advanceToNextAnswerGroup,
} = interviewSlice.actions;

export const interviewActions = interviewSlice.actions;

export default interviewSlice.reducer;
